import { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { ClipboardList, Plus, Archive, Pencil, Hand, X, BookOpen, AlertOctagon, Search, StickyNote, Scale, Library } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { SectionHeader, DLLoading, EmptyState, DLButton, ClassBadge, FlagChip, Slogan } from "@/components/dl/ui";
import { InactiveNotice } from "@/components/dl/EntitlementGate";
import { useAuth } from "@/context/AuthContext";

const MODULES = [
  { id: "case_theory", label: "Case Theory", icon: BookOpen },
  { id: "defense_problem", label: "Defense Problems", icon: AlertOctagon },
  { id: "investigative_assignment", label: "Investigations", icon: Search },
  { id: "evidence_note", label: "Evidence / Testimony", icon: StickyNote },
  { id: "motion", label: "Motion Bank", icon: Scale },
  { id: "research", label: "Research & Precedent", icon: Library },
];
const CLASSES = ["FACT", "TESTIMONY", "ALLEGATION", "INFERENCE", "DISPUTED", "UNKNOWN"];
const CONTESTABLE = ["YES", "NO", "UNKNOWN"];

export default function DLPlaybook() {
  const { caseId, team } = useOutletContext();
  const { user } = useAuth();
  const [module, setModule] = useState("case_theory");
  const [items, setItems] = useState(null);
  const [records, setRecords] = useState([]);
  const [flagsByTarget, setFlagsByTarget] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!team) return;
    setItems(null);
    try {
      const { data } = await api.get(`/dl/teams/${team.dl_team_id}/playbook`, { params: { module } });
      setItems(data);
    } catch { setItems([]); }
  }, [team, module]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (team) api.get(`/dl/cases/${caseId}/record`).then(({ data }) => setRecords(data)).catch(() => {});
    if (team) api.get(`/dl/teams/${team.dl_team_id}/flags`).then(({ data }) => {
      const m = {};
      (data || []).forEach((f) => { (m[f.target_id] = m[f.target_id] || []).push(f); });
      setFlagsByTarget(m);
    }).catch(() => {});
  }, [caseId, team]);

  if (!team) return <InactiveNotice />;

  const openCreate = () => { setEditing(null); setForm({ title: "", detail: "", classification: "UNKNOWN", contestable: "UNKNOWN", source_refs: [] }); setShowForm(true); };
  const openEdit = (it) => { setEditing(it); setForm({ title: it.title, detail: it.data?.detail || "", classification: it.data?.classification || "UNKNOWN", contestable: it.data?.contestable || "UNKNOWN", status: it.status, source_refs: it.source_refs || [] }); setShowForm(true); };

  const toggleRef = (id) => setForm((f) => ({ ...f, source_refs: f.source_refs.includes(id) ? f.source_refs.filter((x) => x !== id) : [...f.source_refs, id] }));

  const submit = async () => {
    setBusy(true);
    const data = { detail: form.detail };
    if (module === "evidence_note") data.classification = form.classification;
    if (module === "defense_problem") data.contestable = form.contestable;
    try {
      if (editing) {
        await api.put(`/dl/playbook/${editing.item_id}`, { title: form.title, data, source_refs: form.source_refs });
        toast.success("Playbook item updated.");
      } else {
        await api.post(`/dl/teams/${team.dl_team_id}/playbook`, { module, title: form.title, data, source_refs: form.source_refs });
        toast.success("Added to the playbook.");
      }
      setShowForm(false); await load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const act = async (fn, ok) => {
    try { await fn(); toast.success(ok); await load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const ActiveMod = MODULES.find((m) => m.id === module);

  return (
    <div className="space-y-6">
      <SectionHeader kicker="The Playbook" title="Build your case."
        desc="Your team's working strategy — mutable and private. Nothing here is committed until you Call and Lock a Play."
        right={<DLButton onClick={openCreate} data-testid="dl-playbook-add"><Plus className="w-4 h-4" /> Add {ActiveMod?.label}</DLButton>} />

      <div className="inline-flex items-center gap-2 rounded-sm border border-amber-500/30 bg-amber-500/5 px-3 py-1.5">
        <Pencil className="w-3.5 h-3.5 text-amber-300" /><span className="text-[11px] font-mono text-amber-200/80 uppercase tracking-wider">Working strategy — editable</span>
      </div>

      {/* Module tabs */}
      <div className="dl-subnav flex gap-2 overflow-x-auto pb-1">
        {MODULES.map((m) => (
          <button key={m.id} onClick={() => setModule(m.id)} data-testid={`dl-pb-mod-${m.id}`}
            className={`shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-sm text-sm transition-colors ${module === m.id ? "bg-white/10 text-gold border border-gold/40" : "text-white/60 hover:text-white border border-white/10"}`}>
            <m.icon className="w-4 h-4" /> {m.label}
          </button>
        ))}
      </div>

      {items === null ? <DLLoading /> :
        items.length === 0 ? (
          <EmptyState icon={ClipboardList} title={`No ${ActiveMod?.label} yet`} desc="Start building this part of your defense." testid="dl-playbook-empty"
            action={<DLButton onClick={openCreate}><Plus className="w-4 h-4" /> Add the first one</DLButton>} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {items.map((it) => (
              <div key={it.item_id} className="dl-panel rounded-md p-4" data-testid="dl-playbook-item">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-display font-semibold text-white">{it.title}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      {module === "evidence_note" && it.data?.classification && <ClassBadge cls={it.data.classification} />}
                      {module === "evidence_note" && it.data?.exact_quote && <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm border ${it.data.exact_quote_verified ? "border-emerald-500/40 text-emerald-300" : "border-white/20 text-white/40"}`}>{it.data.exact_quote_verified ? "QUOTE VERIFIED" : "QUOTE UNVERIFIED"}</span>}
                      {module === "defense_problem" && it.data?.contestable && <span className="text-[10px] font-mono text-white/50 border border-white/15 rounded-sm px-1.5 py-0.5">CONTESTABLE: {it.data.contestable}</span>}
                      {it.status && it.status !== "active" && <span className="text-[10px] font-mono text-white/40 uppercase">{it.status}</span>}
                    </div>
                  </div>
                  <span className="text-[10px] font-mono text-white/30 shrink-0">v{it.version}</span>
                </div>
                {it.data?.detail && <p className="mt-2 text-sm text-white/60 whitespace-pre-wrap">{it.data.detail}</p>}
                {(flagsByTarget[it.item_id] || []).length > 0 && (
                  <div className="mt-3 border-t border-white/5 pt-2" data-testid="dl-item-flags">
                    <div className="text-[9px] font-mono uppercase tracking-wider text-red-300/70 mb-1">Flag on the play · Commissioner QC — not a legal ruling</div>
                    <div className="flex flex-wrap gap-1.5">
                      {(flagsByTarget[it.item_id] || []).map((f) => (
                        <FlagChip key={f.flag_id} type={f.flag_type} note={f.note} resolved={f.resolved} />
                      ))}
                    </div>
                  </div>
                )}
                {(it.source_refs || []).length > 0 && (
                  <div className="mt-2 text-[11px] font-mono text-gold/70">{it.source_refs.length} record source{it.source_refs.length > 1 ? "s" : ""} cited</div>
                )}
                {module === "investigative_assignment" && (
                  <div className="mt-2 text-[11px] font-mono text-white/50">{it.assigned_to_user_id ? (it.assigned_to_user_id === user?.user_id ? "Claimed by you" : "Claimed") : "Unassigned"}</div>
                )}
                <div className="mt-3 flex gap-2">
                  {module === "investigative_assignment" && !it.assigned_to_user_id && (
                    <DLButton variant="ghost" data-testid="dl-pb-claim" onClick={() => act(() => api.post(`/dl/playbook/${it.item_id}/claim`), "Assignment claimed.")}><Hand className="w-3.5 h-3.5" /> Claim</DLButton>
                  )}
                  <DLButton variant="ghost" data-testid="dl-pb-edit" onClick={() => openEdit(it)}><Pencil className="w-3.5 h-3.5" /> Edit</DLButton>
                  {it.status !== "archived" && <DLButton variant="ghost" data-testid="dl-pb-archive" onClick={() => act(() => api.post(`/dl/playbook/${it.item_id}/archive`), "Archived.")}><Archive className="w-3.5 h-3.5" /> Archive</DLButton>}
                </div>
              </div>
            ))}
          </div>
        )}

      {/* Create / edit modal */}
      {showForm && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="dl-panel rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()} data-testid="dl-playbook-form">
            <div className="flex items-center justify-between mb-4">
              <Slogan>{editing ? "Edit" : "Add"} · {ActiveMod?.label}</Slogan>
              <button onClick={() => setShowForm(false)} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <label className="block text-xs text-white/50 mb-1">Title</label>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="dl-pb-title"
              className="w-full bg-charcoal border border-white/15 rounded-sm px-3 py-2 text-white focus:border-gold outline-none mb-3" />
            <label className="block text-xs text-white/50 mb-1">Details</label>
            <textarea value={form.detail} onChange={(e) => setForm({ ...form, detail: e.target.value })} rows={4} data-testid="dl-pb-detail"
              className="w-full bg-charcoal border border-white/15 rounded-sm px-3 py-2 text-white focus:border-gold outline-none mb-3" />
            {module === "evidence_note" && (
              <div className="mb-3">
                <label className="block text-xs text-white/50 mb-1">Classification</label>
                <select value={form.classification} onChange={(e) => setForm({ ...form, classification: e.target.value })} data-testid="dl-pb-classification"
                  className="w-full bg-charcoal border border-white/15 rounded-sm px-3 py-2 text-white focus:border-gold outline-none">
                  {CLASSES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            )}
            {module === "defense_problem" && (
              <div className="mb-3">
                <label className="block text-xs text-white/50 mb-1">Contestable?</label>
                <select value={form.contestable} onChange={(e) => setForm({ ...form, contestable: e.target.value })} data-testid="dl-pb-contestable"
                  className="w-full bg-charcoal border border-white/15 rounded-sm px-3 py-2 text-white focus:border-gold outline-none">
                  {CONTESTABLE.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            )}
            <label className="block text-xs text-white/50 mb-1">Cite Case Record sources <span className="text-white/30">(only admitted records)</span></label>
            <div className="max-h-40 overflow-y-auto border border-white/10 rounded-sm p-2 mb-4">
              {records.length === 0 ? <div className="text-xs text-white/30 py-2 text-center">No admitted records to cite.</div> :
                records.map((r) => (
                  <label key={r.record_id} className="flex items-center gap-2 px-1 py-1.5 text-sm text-white/70 cursor-pointer hover:text-white">
                    <input type="checkbox" checked={form.source_refs.includes(r.record_id)} onChange={() => toggleRef(r.record_id)} className="accent-gold" />
                    {r.title}
                  </label>
                ))}
            </div>
            <div className="flex gap-2 justify-end">
              <DLButton variant="ghost" onClick={() => setShowForm(false)}>Cancel</DLButton>
              <DLButton disabled={busy || !form.title?.trim()} onClick={submit} data-testid="dl-pb-save">{editing ? "Save changes" : "Add to playbook"}</DLButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
